import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { SEO } from './SEO';
import { ToolPageExtras } from '../seo/ToolPageExtras';
import { useAdRedirect, AdToast } from '../hooks/useAdRedirect';
import { AdUnit } from './AdUnit';
import { 
  Barcode, 
  Download, 
  RefreshCw, 
  ArrowLeft, 
  Sliders, 
  Type, 
  Sparkles, 
  Check, 
  Info,
  Lock,
  Scissors
} from 'lucide-react';

// Code 39 Character Pattern Directory
const CODE39_ALPHABET = {
  '0': '000110100', '1': '100100001', '2': '001100001', '3': '101100000',
  '4': '000110001', '5': '100110000', '6': '001110000', '7': '000100101',
  '8': '100100100', '9': '001100100', 'A': '100001001', 'B': '001001001',
  'C': '101001000', 'D': '000011001', 'E': '100011000', 'F': '001011000',
  'G': '000001101', 'H': '100001100', 'I': '001001100', 'J': '000011100',
  'K': '100000011', 'L': '001000011', 'M': '101000010', 'N': '000010011',
  'O': '100010010', 'P': '001010010', 'Q': '000000111', 'R': '100000110',
  'S': '001000110', 'T': '000010110', 'U': '110000001', 'V': '011000001',
  'W': '111000000', 'X': '010010001', 'Y': '110010000', 'Z': '011010000',
  '-': '010000101', '.': '110000100', ' ': '011000100', '$': '010101000',
  '/': '010100010', '+': '010001010', '%': '000101010', '*': '010010100'
};

export const BarcodeGenerator = () => {
  const { withAd, toast } = useAdRedirect();
  const [payload, setPayload] = useState('CODE39-TEST');
  
  // Customization configurations
  const [barHeight, setBarHeight] = useState(100); // 40 to 180
  const [barWidth, setBarWidth] = useState(2); // 1 to 4
  const [wideRatio, setWideRatio] = useState(2.5); // 2.0 to 3.0
  const [showText, setShowText] = useState(true);
  const [fontSize, setFontSize] = useState(14); // 10 to 24
  const [barColor, setBarColor] = useState('#0f172a');
  const [bgColor, setBgColor] = useState('#ffffff');
  const [margin, setMargin] = useState(20); // 10 to 50

  const [isCopied, setIsCopied] = useState(false);
  const [isDownloaded, setIsDownloaded] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const svgRef = useRef(null);
  const canvasRef = useRef(null);

  // Auto uppercase and sanitize input text for standard Code 39
  const handleInputChange = (e) => {
    const rawVal = e.target.value.toUpperCase();
    
    // Check for invalid characters
    const validChars = /^[A-Z0-9\-\.\s\$\/\+\%]*$/;
    if (rawVal === '' || validChars.test(rawVal)) {
      setPayload(rawVal);
      setErrorMsg('');
    } else {
      setErrorMsg('Invalid Code 39 characters. Use A-Z, 0-9, space, and symbols (-, ., $, /, +, %).');
    }
  };

  // Generate the binary array of bars and spaces
  // 1 = bar (black), 0 = space (white)
  const generateBarcodeDescriptor = () => {
    if (!payload) return null;

    // Code 39 begins and ends with quiet '*'
    const textToEncode = `*${payload}*`;
    const elements = [];

    for (let charIndex = 0; charIndex < textToEncode.length; charIndex++) {
      const char = textToEncode[charIndex];
      const pattern = CODE39_ALPHABET[char];
      
      if (!pattern) continue;

      // Pattern has 9 digits: 5 bars, 4 spaces.
      // Index 0, 2, 4, 6, 8 are BARS
      // Index 1, 3, 5, 7 are SPACES
      for (let pIndex = 0; pIndex < 9; pIndex++) {
        const isBar = pIndex % 2 === 0;
        const isWide = pattern[pIndex] === '1';
        
        elements.push({
          type: isBar ? 'bar' : 'space',
          widthFactor: isWide ? wideRatio : 1
        });
      }

      // Add inter-character gap (narrow space) except after the last asterisk
      if (charIndex < textToEncode.length - 1) {
        elements.push({
          type: 'space',
          widthFactor: 1
        });
      }
    }

    return elements;
  };

  const descriptor = generateBarcodeDescriptor();

  // Compute absolute layout dimensions based on descriptor elements
  const calculateLayout = () => {
    if (!descriptor) return { totalWidth: 0, totalHeight: 0 };

    let totalWidth = margin * 2;
    descriptor.forEach(el => {
      totalWidth += el.widthFactor * barWidth;
    });

    const totalHeight = barHeight + margin * 2 + (showText ? fontSize + 8 : 0);
    return { totalWidth, totalHeight };
  };

  const { totalWidth, totalHeight } = calculateLayout();

  // Draw code to PNG canvas for exporting
  const handleExport = (formatType) => {
    if (!descriptor) return;

    const canvas = canvasRef.current || document.createElement('canvas');
    canvas.width = totalWidth;
    canvas.height = totalHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Fill background
    ctx.fillStyle = bgColor;
    ctx.fillRect(0, 0, totalWidth, totalHeight);

    // Draw bars
    let currentX = margin;
    descriptor.forEach(el => {
      const elWidth = el.widthFactor * barWidth;
      if (el.type === 'bar') {
        ctx.fillStyle = barColor;
        ctx.fillRect(currentX, margin, elWidth, barHeight);
      }
      currentX += elWidth;
    });

    // Draw text label
    if (showText) {
      ctx.fillStyle = barColor;
      ctx.font = `bold ${fontSize}px sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'top';
      ctx.fillText(payload, totalWidth / 2, barHeight + margin + 8);
    }

    // Direct download action
    const link = document.createElement('a');
    link.download = `barcode-${payload || 'code39'}.${formatType}`;
    link.href = canvas.toDataURL(`image/${formatType === 'jpg' ? 'jpeg' : 'png'}`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    setIsDownloaded(true);
    setTimeout(() => setIsDownloaded(false), 3000);
  };

  // Preset payload samples
  const applyPreset = (preset) => {
    setPayload(preset);
    setErrorMsg('');
  };

  return (
    <div style={{
      maxWidth: '1200px',
      margin: '0 auto',
      width: '100%',
      paddingBottom: '60px'
    }}>
      <SEO 
        title="Free Online Barcode Generator" 
        description="Generate 1D barcodes instantly. Create high-quality Code 39 barcodes for SKU, ISBN, and packaging. Fast, secure, and 100% free client-side tool."
        keywords="barcode generator, online barcode creator, create code 39, free barcode maker, sku generator, packaging barcode, high-res barcode vector"
      />
      
      {/* Hidden virtual canvas for exports */}
      <canvas ref={canvasRef} style={{ display: 'none' }}></canvas>

      {/* Breadcrumbs Navigation */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginBottom: '24px',
        fontSize: '0.9rem'
      }}>
        <Link to="/" style={{
          color: 'var(--text-secondary)',
          textDecoration: 'none',
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          transition: 'var(--transition-smooth)'
        }} className="breadcrumb-back">
          <ArrowLeft size={16} /> Dashboard
        </Link>
        <span style={{ color: 'var(--text-muted)' }}>/</span>
        <span style={{ color: 'var(--primary)', fontWeight: 600 }}>Barcode Generator</span>
      </div>



      {/* Main Workspace Layout */}
      <div className="barcode-workspace-grid" style={{
        display: 'grid',
        gap: '32px',
        alignItems: 'start'
      }}>
        
        {/* Left Column: Interactive Settings & Inputs */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '28px', minWidth: 0, width: '100%' }}>
          
          {/* Main Encoder Form Card */}
          <div style={cardStyle}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Type size={18} style={{ color: 'var(--primary)' }} />
              <h3 style={{ fontSize: '1.15rem', fontWeight: 800, color: 'var(--text-primary)', margin: 0 }}>Alphanumeric Data Input</h3>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <input 
                type="text"
                value={payload}
                onChange={handleInputChange}
                placeholder="E.G. SKU-987452-A"
                maxLength={30}
                style={{
                  width: '100%',
                  padding: '14px 18px',
                  borderRadius: '12px',
                  border: errorMsg ? '1.5px solid #ef4444' : '1px solid var(--border-color)',
                  fontSize: '1rem',
                  fontFamily: 'monospace',
                  fontWeight: 700,
                  outline: 'none',
                  background: 'var(--bg-secondary)',
                  color: 'var(--text-primary)',
                  boxSizing: 'border-box',
                  transition: 'border-color 0.15s'
                }}
              />
              {errorMsg && (
                <span style={{ fontSize: '0.8rem', color: '#ef4444', fontWeight: 600 }}>{errorMsg}</span>
              )}

              {/* Presets suggestions */}
              <div>
                <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase', display: 'block', marginBottom: '8px' }}>Quick Presets Suggestions</span>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {['SKU-987425', 'ASIN-B0892', 'BOX-004-A', 'SERIAL-8819X', 'PASS-99A'].map((p) => (
                    <button
                      key={p}
                      onClick={() => applyPreset(p)}
                      style={{
                        background: payload === p ? 'rgba(5, 117, 251, 0.05)' : '#ffffff',
                        color: payload === p ? 'var(--primary)' : 'var(--text-secondary)',
                        border: payload === p ? '1.5px solid var(--primary)' : '1px solid var(--border-color)',
                        borderRadius: '8px',
                        padding: '6px 14px',
                        fontSize: '0.75rem',
                        fontWeight: 700,
                        cursor: 'pointer',
                        transition: 'var(--transition-smooth)'
                      }}
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Core Customization sliders card */}
          <div style={cardStyle}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Sliders size={18} style={{ color: 'var(--primary)' }} />
              <h3 style={{ fontSize: '1.15rem', fontWeight: 800, color: 'var(--text-primary)', margin: 0 }}>Design Adjustments</h3>
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(min(220px, 100%), 1fr))',
              gap: '24px'
            }}>
              {/* 1. Bar Width factor */}
              <div style={inputGroupStyle}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <label style={labelStyle}>Narrow Bar Width</label>
                  <span style={valBadgeStyle}>{barWidth}px</span>
                </div>
                <input 
                  type="range"
                  min="1"
                  max="4"
                  step="1"
                  value={barWidth}
                  onChange={(e) => setBarWidth(parseInt(e.target.value))}
                  style={{ width: '100%', accentColor: 'var(--primary)', cursor: 'pointer' }}
                />
              </div>

              {/* 2. Wide ratio factor */}
              <div style={inputGroupStyle}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <label style={labelStyle}>Wide-to-Narrow Ratio</label>
                  <span style={valBadgeStyle}>{wideRatio.toFixed(1)}x</span>
                </div>
                <input 
                  type="range"
                  min="2.0"
                  max="3.0"
                  step="0.1"
                  value={wideRatio}
                  onChange={(e) => setWideRatio(parseFloat(e.target.value))}
                  style={{ width: '100%', accentColor: 'var(--primary)', cursor: 'pointer' }}
                />
              </div>

              {/* 3. Bar Height slider */}
              <div style={inputGroupStyle}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <label style={labelStyle}>Barcode Height</label>
                  <span style={valBadgeStyle}>{barHeight}px</span>
                </div>
                <input 
                  type="range"
                  min="40"
                  max="180"
                  step="5"
                  value={barHeight}
                  onChange={(e) => setBarHeight(parseInt(e.target.value))}
                  style={{ width: '100%', accentColor: 'var(--primary)', cursor: 'pointer' }}
                />
              </div>

              {/* 4. Canvas quiet zone margin */}
              <div style={inputGroupStyle}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <label style={labelStyle}>Quiet Zone Padding</label>
                  <span style={valBadgeStyle}>{margin}px</span>
                </div>
                <input 
                  type="range"
                  min="10"
                  max="50"
                  step="2"
                  value={margin}
                  onChange={(e) => setMargin(parseInt(e.target.value))}
                  style={{ width: '100%', accentColor: 'var(--primary)', cursor: 'pointer' }}
                />
              </div>

              {/* 5. Alphanumeric Label Toggle */}
              <div style={inputGroupStyle}>
                <label style={labelStyle}>Show Code Text Label</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', height: '38px' }}>
                  <input 
                    type="checkbox"
                    checked={showText}
                    onChange={(e) => setShowText(e.target.checked)}
                    id="show-label-checkbox"
                    style={{
                      width: '18px',
                      height: '18px',
                      cursor: 'pointer',
                      accentColor: 'var(--primary)'
                    }}
                  />
                  <label htmlFor="show-label-checkbox" style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-secondary)', cursor: 'pointer' }}>
                    Render text under barcode
                  </label>
                </div>
              </div>

              {/* 6. Text Font Size */}
              {showText && (
                <div style={inputGroupStyle}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <label style={labelStyle}>Text Font Size</label>
                    <span style={valBadgeStyle}>{fontSize}px</span>
                  </div>
                  <input 
                    type="range"
                    min="10"
                    max="24"
                    step="1"
                    value={fontSize}
                    onChange={(e) => setFontSize(parseInt(e.target.value))}
                    style={{ width: '100%', accentColor: 'var(--primary)', cursor: 'pointer' }}
                  />
                </div>
              )}
            </div>

            <hr style={{ border: 'none', borderTop: '1px solid var(--border-color)', margin: '8px 0' }} />

            {/* Color Customizations row */}
            <div className="responsive-grid-2col" style={{
              display: 'grid',
              gap: '16px'
            }}>
              <div style={inputGroupStyle}>
                <label style={labelStyle}>Bar Line Color</label>
                <div style={colorPickerContainerStyle}>
                  <input type="color" value={barColor} onChange={(e) => setBarColor(e.target.value)} style={colorInputStyle} />
                  <span style={colorHexLabelStyle}>{barColor.toUpperCase()}</span>
                </div>
              </div>

              <div style={inputGroupStyle}>
                <label style={labelStyle}>Background Color</label>
                <div style={colorPickerContainerStyle}>
                  <input type="color" value={bgColor} onChange={(e) => setBgColor(e.target.value)} style={colorInputStyle} />
                  <span style={colorHexLabelStyle}>{bgColor.toUpperCase()}</span>
                </div>
              </div>
            </div>

          </div>

        </div>

        {/* Right Column: High Fidelity crystal vector Preview card */}
        <div style={{
          position: 'sticky',
          top: '110px',
          display: 'flex',
          flexDirection: 'column',
          gap: '24px',
          width: '100%',
          maxWidth: '400px',
          margin: '0 auto'
        }} className="barcode-preview-column">
          
          {/* Visual Canvas Render Card */}
          <div style={{
            background: '#ffffff',
            border: '1px solid var(--border-color)',
            borderRadius: '20px',
            padding: '24px',
            boxShadow: 'var(--shadow-md)',
            textAlign: 'center'
          }}>
            <span style={{
              fontSize: '0.8rem',
              fontWeight: 800,
              color: 'var(--text-secondary)',
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
              display: 'block',
              textAlign: 'left',
              marginBottom: '20px'
            }}>
              Scalable Vector Output
            </span>

            {/* Outer Frame Wrapper with transparent background checks */}
            <div style={{
              background: 'repeating-conic-gradient(#f8fafc 0% 25%, #f1f5f9 0% 50%) 50% / 20px 20px',
              border: '1px solid var(--border-color)',
              borderRadius: '12px',
              padding: '24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflowX: 'auto',
              minHeight: '220px',
              boxSizing: 'border-box'
            }} className="barcode-canvas-scroll">
              
              {descriptor ? (
                /* Pure Vector SVG Barcode */
                <svg
                  ref={svgRef}
                  width={totalWidth}
                  height={totalHeight}
                  style={{
                    background: bgColor,
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(15, 23, 42, 0.02)',
                    flexShrink: 0
                  }}
                >
                  {/* Fill Background */}
                  <rect width={totalWidth} height={totalHeight} fill={bgColor} />
                  
                  {/* Render alternate bars */}
                  {(() => {
                    let currentX = margin;
                    return descriptor.map((el, idx) => {
                      const elWidth = el.widthFactor * barWidth;
                      const xPos = currentX;
                      currentX += elWidth;

                      if (el.type === 'bar') {
                        return (
                          <rect
                            key={idx}
                            x={xPos}
                            y={margin}
                            width={elWidth}
                            height={barHeight}
                            fill={barColor}
                          />
                        );
                      }
                      return null;
                    });
                  })()}

                  {/* Render Alphanumeric code below */}
                  {showText && (
                    <text
                      x={totalWidth / 2}
                      y={barHeight + margin + 8}
                      fill={barColor}
                      fontFamily="sans-serif"
                      fontWeight="bold"
                      fontSize={fontSize}
                      textAnchor="middle"
                      alignmentBaseline="hanging"
                    >
                      {payload}
                    </text>
                  )}
                </svg>
              ) : (
                <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>
                  Please enter alphanumeric values...
                </div>
              )}

            </div>

            {/* Quick specifications breakdown */}
            <div style={{
              marginTop: '16px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              fontSize: '0.78rem',
              color: 'var(--text-secondary)'
            }}>
              <span>Width: <strong>{totalWidth}px</strong></span>
              <span>Height: <strong>{totalHeight}px</strong></span>
              <span>Characters: <strong>{payload.length}</strong></span>
            </div>

            <hr style={{ border: 'none', borderTop: '1px solid var(--border-color)', margin: '16px 0' }} />

            {/* Action buttons list */}
            <AdToast message={toast} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <button
                onClick={withAd('barcode-png', () => handleExport('png'))}
                disabled={!payload}
                style={{
                  background: isDownloaded ? '#059669' : 'linear-gradient(90deg, var(--secondary) 0%, var(--primary) 100%)',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '10px',
                  padding: '12px',
                  fontSize: '0.9rem',
                  fontWeight: 700,
                  cursor: !payload ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  transition: 'var(--transition-smooth)'
                }}
                className="download-compressed-btn"
              >
                <Download size={16} /> {isDownloaded ? 'Downloaded PNG!' : 'Download High-Res PNG'}
              </button>

              <button
                onClick={withAd('barcode-jpg', () => handleExport('jpg'))}
                disabled={!payload}
                style={{
                  background: '#ffffff',
                  color: 'var(--secondary)',
                  border: '1px solid var(--secondary)',
                  borderRadius: '10px',
                  padding: '11px',
                  fontSize: '0.9rem',
                  fontWeight: 700,
                  cursor: !payload ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  transition: 'var(--transition-smooth)'
                }}
                className="open-link-btn"
              >
                <Download size={16} /> Download JPEG format
              </button>
            </div>

          </div>



        </div>

      </div>

      {/* Workspace design styles */}
      <style>{`
        @media (min-width: 992px) {
          .barcode-workspace-grid {
            grid-template-columns: minmax(0, 1fr) 400px !important;
          }
        }
        @media (max-width: 991px) {
          .barcode-workspace-grid {
            grid-template-columns: minmax(0, 1fr) !important;
          }
        }

        .download-compressed-btn:hover {
          transform: translateY(-1px);
          box-shadow: 0 8px 20px -2px rgba(5, 117, 251, 0.45) !important;
          opacity: 0.95;
        }

        .open-link-btn:hover {
          background: rgba(14, 165, 233, 0.04) !important;
          transform: translateY(-1px);
        }
      `}</style>

    {/* SEO: FAQ & Related Tools */}
      <AdUnit />
      <ToolPageExtras toolId="barcode-generator" />
    </div>
  );
};

// Styles descriptors
const cardStyle = {
  background: '#ffffff',
  border: '1px solid var(--border-color)',
  borderRadius: '20px',
  padding: '24px',
  display: 'flex',
  flexDirection: 'column',
  gap: '18px',
  boxShadow: 'var(--shadow-sm)'
};

const inputGroupStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '8px'
};

const labelStyle = {
  fontSize: '0.75rem',
  fontWeight: 800,
  color: 'var(--text-secondary)',
  textTransform: 'uppercase',
  letterSpacing: '0.04em'
};

const valBadgeStyle = {
  fontSize: '0.825rem',
  fontWeight: 800,
  color: 'var(--primary)',
  background: 'rgba(5, 117, 251, 0.06)',
  padding: '2px 8px',
  borderRadius: '6px'
};

const colorPickerContainerStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  background: 'var(--bg-secondary)',
  border: '1px solid var(--border-color)',
  padding: '8px 12px',
  borderRadius: '8px'
};

const colorInputStyle = {
  border: 'none',
  outline: 'none',
  width: '32px',
  height: '32px',
  borderRadius: '4px',
  cursor: 'pointer',
  padding: 0,
  background: 'transparent'
};

const colorHexLabelStyle = {
  fontSize: '0.85rem',
  fontWeight: 700,
  fontFamily: 'monospace',
  color: 'var(--text-primary)'
};

export default BarcodeGenerator;
